import { alpha, Box, Theme, Tooltip, Typography } from "@mui/material";

export type EquipmentTone =
	| "ok"
	| "alert"
	| "info"
	| "warn"
	| "cleaning"
	| "disabled"
	| "unknown";

const TONE_COLOR = {
	ok: (t: Theme) => t.palette.success.main,
	alert: (t: Theme) => t.palette.error.main,
	warn: (t: Theme) => t.palette.warning.main,
	info: (t: Theme) => t.palette.info.main,
	cleaning: (_t: Theme) => "#9C27B0",
	disabled: (_t: Theme) => "#616161",
	unknown: (_t: Theme) => "#BDBDBD",
} as const;

type Props = {
	label: string;
	tone: EquipmentTone;
	tooltip?: string;
	/**
	 * Optional short value rendered inside the colored square
	 * (e.g. "200", "LO", count). If absent, the square stays empty
	 * which is the original on/off indicator look.
	 */
	value?: string | number;
};

export const EquipmentBox = ({ label, tone, tooltip, value }: Props) => {
	const color = TONE_COLOR[tone];

	const content = (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				px: 0.5,
				py: 0.4,
				minWidth: 36,
				borderRadius: 1,
				backgroundColor: (theme) => alpha(color(theme), 0.01),
				border: (theme) => `1px solid ${alpha(color(theme), 0.2)}`,
				transition: "all 0.2s ease",
				cursor: "default",
				"&:hover": {
					backgroundColor: (theme) => alpha(color(theme), 0.15),
					borderColor: (theme) => alpha(color(theme), 0.4),
					transform: "translateY(-1px)",
				},
			}}
		>
			<Box
				sx={{
					minWidth: 22,
					height: 22,
					backgroundColor: tone === "unknown" ? "#fff" : color,
					border: tone === "unknown" ? "1px dashed #BDBDBD" : "none",
					borderRadius: 1,
					mb: 0.25,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					px: value !== undefined ? 0.5 : 0,
				}}
			>
				{value !== undefined && (
					<Typography
						sx={{
							fontSize: "0.65rem",
							fontWeight: 700,
							color: tone === "unknown" ? "#757575" : "#fff",
							lineHeight: 1,
						}}
					>
						{value}
					</Typography>
				)}
			</Box>
			<Typography
				sx={{
					fontSize: "0.55rem",
					fontWeight: 600,
					color: (theme) => alpha(theme.palette.text.primary, 0.8),
					whiteSpace: "nowrap",
				}}
			>
				{label}
			</Typography>
		</Box>
	);

	if (!tooltip) return content;

	return (
		<Tooltip title={tooltip} arrow enterDelay={300}>
			{content}
		</Tooltip>
	);
};
